'use client'

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { useTranslations } from 'next-intl'
import { List, Grid3X3, GalleryHorizontalEnd } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type StepViewMode = 'list' | 'grid' | 'carousel'

const STORAGE_KEY = 'theduchy.step-view-preference'
const DEFAULT_MODE: StepViewMode = 'list'

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

interface StepViewContextValue {
  mode: StepViewMode
  setMode: (mode: StepViewMode) => void
  /** True only inside a real StepViewProvider — lets consumers fall back to
   *  local state when rendered without one (e.g. a step block on a page that
   *  doesn't mount the provider). */
  provided: boolean
}

const StepViewContext = createContext<StepViewContextValue>({
  mode: DEFAULT_MODE,
  setMode: () => {},
  provided: false,
})

export function useStepViewPreference() {
  return useContext(StepViewContext)
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

interface StepViewProviderProps {
  children: ReactNode
  /**
   * SSR-fetched preference from `profiles.step_view_default` for the
   * signed-in user. When provided, it wins over localStorage on mount —
   * so a mode change made on mobile shows up immediately on the next
   * web page load. Pass `null` for unauthed users or when the column
   * is null (no explicit preference).
   *
   * Kept optional + null-safe so existing callers that don't yet pass
   * it keep working with localStorage-only behaviour.
   */
  initialMode?: StepViewMode | null
  /** Whether the current visitor is authed; controls whether mode
   *  changes persist back to the server via /api/account/step-view. */
  authed?: boolean
}

export function StepViewProvider({
  children,
  initialMode,
  authed = false,
}: StepViewProviderProps) {
  const [mode, setModeState] = useState<StepViewMode>(initialMode ?? DEFAULT_MODE)

  // Hydrate from localStorage on mount, but only when there's no
  // server-provided initialMode — server preference wins so the user
  // sees the same default they set in the mobile app.
  useEffect(() => {
    try {
      if (initialMode == null) {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored === 'list' || stored === 'grid' || stored === 'carousel') {
          setModeState(stored)
        }
      }
    } catch {
      // localStorage unavailable (private browsing, etc.)
    }
    // initialMode is intentionally not in deps — we only honor it on first
    // mount so subsequent client-side mode changes win.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setMode = useCallback(
    (next: StepViewMode) => {
      setModeState(next)
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // localStorage unavailable
      }
      // Persist to server so the mobile app picks up the change next launch.
      // Fire-and-forget: a server-side failure shouldn't break the UI swap.
      if (authed) {
        fetch('/api/account/step-view', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: next }),
        }).catch(() => {
          /* network down, server error — local state still updated */
        })
      }
    },
    [authed],
  )

  return (
    <StepViewContext.Provider value={{ mode, setMode, provided: true }}>
      {children}
    </StepViewContext.Provider>
  )
}

/* ------------------------------------------------------------------ */
/*  Picker (toggle buttons)                                            */
/* ------------------------------------------------------------------ */

// `labelKey` → articleView.view* (localized in StepViewPicker).
const modes: { value: StepViewMode; labelKey: 'viewList' | 'viewGrid' | 'viewCarousel'; icon: typeof List }[] = [
  { value: 'list', labelKey: 'viewList', icon: List },
  { value: 'grid', labelKey: 'viewGrid', icon: Grid3X3 },
  { value: 'carousel', labelKey: 'viewCarousel', icon: GalleryHorizontalEnd },
]

export function StepViewPicker({
  mode: modeProp,
  onChange,
}: {
  /** Controlled mode. When omitted, the picker reads the shared context. */
  mode?: StepViewMode
  /** Controlled setter. When omitted, writes to the shared context. */
  onChange?: (mode: StepViewMode) => void
} = {}) {
  const t = useTranslations('articleView')
  const ctx = useStepViewPreference()
  const mode = modeProp ?? ctx.mode
  const setMode = onChange ?? ctx.setMode

  return (
    <div className="inline-flex items-center gap-1 border bg-gray-50 p-0.5">
      {modes.map(({ value, labelKey, icon: Icon }) => (
        <button
          key={value}
          onClick={() => setMode(value)}
          aria-label={t('viewAria', { label: t(labelKey) })}
          aria-pressed={mode === value}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${
            mode === value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{t(labelKey)}</span>
        </button>
      ))}
    </div>
  )
}
